<template>
  <b-card no-body>
    <b-card-body id="customers-card-body">
      <customer-filter
        :filter-submitting="isLoading"
        @filter="setFilter"
        @status-options-loaded="setCustomerStatusOptions"
      />

      <vue-good-table
        mode="remote"
        :sort-options="{ enabled: false }"
        :total-rows="totalRecords"
        :is-loading.sync="isLoading"
        :show-footer="true"
        :rows="rows"
        :columns="getColumns"
        :pagination-options="{
          enabled: true,
          jumpFirstOrLast: true,
          rowsPerPageLabel: t('Rows per page'),
          ofLabel: t('ofLabel'),
          nextLabel: t('nextLabel'),
          prevLabel: t('prevLabel'),
          dropdownAllowAll: false
        }"
        @on-page-change="onPageChange"
        @on-per-page-change="onPerPageChange"
      >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'type'">
            {{ mapCustomerType(props.row.type) }}
          </span>
          <span v-else-if="props.column.field === 'name'">
            {{ props.row.name || '-' }}
          </span>
          <span v-else-if="props.column.field === 'email'">
            {{ props.row.email || '-' }}
          </span>
          <span v-else-if="props.column.field === 'status'">
            <b-badge pill :variant="mapStatusVariant(props.row.status)">
              {{ mapCustomerStatus(props.row.status) }}
            </b-badge>
          </span>
          <span v-else-if="props.column.field === 'approvalStatus'">
            <b-badge pill :variant="mapApprovalStatusVariant(getApprovalStatusValue(props.row))">
              {{ mapApprovalStatus(getApprovalStatusValue(props.row)) }}
            </b-badge>
          </span>
          <span v-else-if="props.column.field === 'attention_name'">
            {{ props.row.authorizedName || '-' }}
          </span>
          <span v-else-if="props.column.field === 'created_at'">
            {{ props.row.created_at || '-' }}
          </span>
          <span v-else-if="props.column.field === 'updated_at'">
            {{ props.row.updated_at || '-' }}
          </span>

          <span v-else-if="props.column.field === 'actions'">
            <span class="d-flex justify-content-center table-icon-buttons">
              <b-button
                :id="`edit-button-${props.row.id}`"
                variant="primary"
                type="button"
                size="sm"
                @click="detailCustomer(props.row.id)"
              >
                <feather-icon icon="Edit3Icon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`edit-button-${props.row.id}`"
                  :title="t('Edit Customers Infos')"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>
              <b-button
                :id="`delete-customer-button-${props.row.id}`"
                variant="danger"
                type="button"
                size="sm"
                @click="deleteCustomer(props.row.id)"
              >
                <feather-icon icon="XIcon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`delete-customer-button-${props.row.id}`"
                  :title="t('Delete Customer')"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>
              <b-dropdown size="sm" variant="secondary" right dropup stop>
                <template #button-content>
                  <feather-icon icon="settings" size="16" class="mr-1" />
                  <b>{{ t('Others') }}</b>
                </template>
                <b-dropdown-item @click="customerDocuments(props.row.id)">
                  {{ t('Customers Documents') }}
                </b-dropdown-item>
                <b-dropdown-item @click="detailCustomerParameters(props.row.id)">
                  {{ t('Customers Parameters') }}
                </b-dropdown-item>
                <b-dropdown-item @click="customerUsers(props.row.id)">
                  {{ t('Users') }}
                </b-dropdown-item>
                <b-dropdown-item @click="customerAssigneProvider(props.row.id)">
                  {{ t('Assign Service Provider') }}
                </b-dropdown-item>
                <b-dropdown-item @click="createCustomerPaymentIntent(props.row.id)">
                  {{ t('Create Payment Intent') }}
                </b-dropdown-item>
                <b-dropdown-item @click="customerDestinationAuthorization(props.row.id)">
                  {{ t('Destination Authorization') }}
                </b-dropdown-item>
              </b-dropdown>
            </span>
          </span>
        </template>
      </vue-good-table>
    </b-card-body>
    <create-customer-payment-intent :customer-id="customerId" />
  </b-card>
</template>

<script>
import {
  BCard,
  BCardBody,
  BDropdown,
  BDropdownItem,
  BTooltip,
  BButton,
  BBadge
} from 'bootstrap-vue';
import { VueGoodTable } from 'vue-good-table';
import { helper } from '@core/mixins/helper.js';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import CustomerFilter from '@/views/pages/customers/CustomerFilter.vue';
import useService from '@/auth/service/useService';
import CreateCustomerPaymentIntent from './CreateCustomerPaymentIntent.vue';

export default {
  components: {
    VueGoodTable,
    BCard,
    BCardBody,
    BDropdown,
    BDropdownItem,
    BTooltip,
    BButton,
    BBadge,
    CustomerFilter,
    CreateCustomerPaymentIntent
  },
  mixins: [helper],
  data() {
    return {
      isLoading: false,
      rows: [],
      customerStatusTypes: [],
      totalRecords: 0,
      filter: {
        page: 1,
        limit: 10
      },
      filterValues: {
        name: '',
        approvalStatus: '',
        status: ''
      },
      customerId: null
    };
  },
  computed: {
    getColumns() {
      return [
        {
          label: 'Müşteri Tipi',
          field: 'type'
        },
        {
          label: this.$t('Name'),
          field: 'name'
        },
        {
          label: this.$t('Email'),
          field: 'email'
        },
        {
          label: this.$t('Usage Status'),
          field: 'status'
        },
        {
          label: this.$t('Account Approval'),
          field: 'approvalStatus'
        },
        {
          label: this.$t('Actions'),
          field: 'actions'
        }
      ];
    }
  },
  async created() {
    this.loadItems();
  },
  methods: {
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },
    mapCustomerType(value) {
      const v = parseInt(value, 10);
      if (v === 1) return this.$t('Personal');
      if (v === 2) return this.$t('Business');
      return '-';
    },
    setCustomerStatusOptions(options) {
      const list = Array.isArray(options) ? options : [];
      this.customerStatusTypes = list.map(i => ({
        text: i && (i.text || i.label) ? String(i.text || i.label).replace(/^__/, '') : '',
        value: i?.value
      }));
    },
    mapCustomerStatus(value) {
      const found = (this.customerStatusTypes || []).find(i => i.value === value);
      return found ? found.text : '-';
    },
    mapStatusVariant(value) {
      if (value === 1) return 'success'; // ACTIVE
      if (value === 0) return 'danger'; // INACTIVE
      if (value === 99) return 'info'; // MANAGER_COMPANY
      return 'secondary';
    },
    getApprovalStatusValue(row) {
      if (!row || !row.defaultLedgerAccount) return null;
      const status = row.defaultLedgerAccount.approvalStatus;
      return typeof status === 'number' ? status : null;
    },
    mapApprovalStatus(status) {
      if (status === 0) return this.$t('Rejected');
      if (status === 1) return this.$t('Approved');
      if (status === 2) return this.$t('Pending');
      if (status === 3) return this.$t('In Review');
      return '-';
    },
    mapApprovalStatusVariant(status) {
      if (status === 1) return 'success'; // Approved
      if (status === 0) return 'danger'; // Rejected
      if (status === 2) return 'warning'; // Pending
      if (status === 3) return 'info'; // In Review
      return 'secondary';
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    loadItems() {
      let requestData = this.filter;

      requestData = {
        ...requestData,
        ...this.getFilteredObject(this.filterValues)
      };

      if (
        requestData.dateFrom &&
        requestData.dateTo &&
        new Date(requestData.dateFrom) > new Date(requestData.dateTo)
      ) {
        this.showErrorMessage(this.$t('Big Date Error'), this.$t('Unsuccessful'));
        return;
      }

      this.isLoading = true;

      useService
        .getAllCustomers(requestData)
        .then(response => {
          this.rows = response.data;
          this.totalRecords = response.meta.total;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    setFilter(data) {
      if (data) {
        this.filterValues = data;
        this.filter.page = 1;
        this.loadItems();
      } else {
        this.filterValues = {
          name: '',
          approvalStatus: '',
          status: ''
        };
        this.filter.page = 1;
        this.loadItems();
      }
    },

    customerPrices(id) {
      this.$router.push(`/customer_prices/${id}`);
    },

    detailCustomer(id) {
      this.$router.push(`/customers/${id}`);
    },

    deleteCustomer(id) {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Customer Delete Warning')}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(async result => {
        if (result.value) {
          await this.deleteCustomerRequest(id)
            .then(response => {
              this.showSuccessMessage(`${response.message || this.$t('Customer was deleted')}`);

              this.filter.page = 1;
              this.loadItems();
            })
            .catch(messages => {
              if (typeof messages === 'object' && messages && messages.length) {
                messages.forEach(item => {
                  this.showErrorMessage(item.message);
                });
              }
            });
        }
      });
    },

    async deleteCustomerRequest(id) {
      return useService.deleteCustomer(id);
    },

    async customerUsers(id) {
      this.$router.push(`/customers/${id}/users`);
    },
    async customerDocuments(id) {
      this.$router.push(`/customers/${id}/documents`);
    },
    async customerAssigneProvider(id) {
      this.$router.push(`/customers/${id}/assigne-provider`);
    },

    async detailCustomerParameters(id) {
      this.$router.push(`/customers/${id}/parameters`);
    },

    async createCustomerPaymentIntent(id) {
      this.customerId = id;
      this.$bvModal.show('create-customer-payment-intent');
    },

    async customerDestinationAuthorization(id) {
      this.$router.push(`/customers/${id}/destination-authorization`);
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
